import React, { useState, useEffect, useCallback } from "react";
import adminAxiosInstance from "../utils/adminAxios";
import { API_ENDPOINTS } from "../config";

/**
 * Book-wise and chapter-wise usage, for the admin analytics page.
 *
 * Three levels, each fetched only when it is opened:
 *
 *   book      → how many users subscribed to it, how many of its chapters have
 *               been started, and by how many students
 *   chapter   → how many students started and finished it
 *   top users → the five students who answered the most questions in that chapter
 *
 * The chapter and leaderboard queries are deliberately lazy. Loading every
 * chapter of every book up front would ask the 512MB backend for exactly the
 * per-student fan-out the analytics routes exist to avoid — see the memory
 * contract at the top of routes/analyticsRoutes.js. Responses are kept in
 * state so re-opening a row costs nothing.
 */

// Columns the backend will sort on. Anything else is ignored server-side.
const BOOK_COLUMNS = [
  // `align` is a full class name, not a fragment: Tailwind only keeps classes
  // it can see literally in the source, so `text-${...}` would be purged.
  { field: "title", label: "Book", align: "text-left" },
  { field: "subscribers", label: "Subscribed", align: "text-right" },
  { field: "chaptersStarted", label: "Chapters started", align: "text-right" },
  { field: "learners", label: "Students", align: "text-right" },
  { field: "questionsAnswered", label: "Questions", align: "text-right" },
  { field: "averageScorePercent", label: "Avg score", align: "text-right" },
];

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

const BookChapterAnalytics = ({ publisher }) => {
  const [books, setBooks] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("subscribers");
  const [order, setOrder] = useState("desc");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // bookId → { loading, error, rows, summary }
  const [chapters, setChapters] = useState({});
  const [openBook, setOpenBook] = useState(null);

  // chapterId → { loading, error, rows }
  const [topUsers, setTopUsers] = useState({});
  const [openChapter, setOpenChapter] = useState(null);

  const loadBooks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminAxiosInstance.get(API_ENDPOINTS.ANALYTICS_BOOKS, {
        params: {
          page, limit: 25, sortBy, order,
          ...(search ? { search } : {}),
          ...(publisher ? { publisher } : {}),
        },
      });
      setBooks(data.data || []);
      setPagination(data.pagination || null);
    } catch (e) {
      setBooks([]);
      setError(e.response?.data?.error || e.message || "Could not load books");
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, order, search, publisher]);

  useEffect(() => { loadBooks(); }, [loadBooks]);

  // Switching app changes what every cached row means, so drop the open
  // expansions rather than show one publisher's chapters under another's book.
  useEffect(() => {
    setChapters({});
    setTopUsers({});
    setOpenBook(null);
    setOpenChapter(null);
    setPage(1);
  }, [publisher]);

  const toggleBook = async (bookId) => {
    if (openBook === bookId) {
      setOpenBook(null);
      return;
    }
    setOpenBook(bookId);
    setOpenChapter(null);
    if (chapters[bookId]) return; // already fetched

    setChapters((c) => ({ ...c, [bookId]: { loading: true } }));
    try {
      const { data } = await adminAxiosInstance.get(
        API_ENDPOINTS.ANALYTICS_BOOK_CHAPTERS(bookId),
        { params: publisher ? { publisher } : {} }
      );
      setChapters((c) => ({
        ...c,
        [bookId]: { loading: false, rows: data.data || [], summary: data.book || null },
      }));
    } catch (e) {
      setChapters((c) => ({
        ...c,
        [bookId]: {
          loading: false,
          rows: [],
          error: e.response?.data?.error || "Could not load chapters",
        },
      }));
    }
  };

  const toggleChapter = async (chapterId) => {
    if (openChapter === chapterId) {
      setOpenChapter(null);
      return;
    }
    setOpenChapter(chapterId);
    if (topUsers[chapterId]) return;

    setTopUsers((t) => ({ ...t, [chapterId]: { loading: true } }));
    try {
      const { data } = await adminAxiosInstance.get(
        API_ENDPOINTS.ANALYTICS_CHAPTER_TOP_USERS(chapterId),
        { params: { limit: 5, ...(publisher ? { publisher } : {}) } }
      );
      setTopUsers((t) => ({ ...t, [chapterId]: { loading: false, rows: data.data || [] } }));
    } catch (e) {
      setTopUsers((t) => ({
        ...t,
        [chapterId]: {
          loading: false,
          rows: [],
          error: e.response?.data?.error || "Could not load leaderboard",
        },
      }));
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      // Names read best A→Z; every other column is "most first".
      setOrder(field === "title" ? "asc" : "desc");
    }
    setPage(1);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const renderTopUsers = (chapterId) => {
    const state = topUsers[chapterId];
    if (!state || state.loading) {
      return <p className="text-xs text-gray-400 py-2">Loading top students…</p>;
    }
    if (state.error) {
      return <p className="text-xs text-red-600 py-2">{state.error}</p>;
    }
    if (!state.rows.length) {
      return <p className="text-xs text-gray-400 py-2">No questions answered in this chapter yet.</p>;
    }
    return (
      <div>
        <p className="text-xs font-semibold text-gray-600 mb-2">
          Top {state.rows.length} students by questions attempted
        </p>
        <table className="w-full text-xs">
          <thead className="text-gray-500">
            <tr>
              <th className="text-left px-2 py-1 w-8">#</th>
              <th className="text-left px-2 py-1">Student</th>
              <th className="text-right px-2 py-1">Questions</th>
              <th className="text-right px-2 py-1">Attempts</th>
              <th className="text-right px-2 py-1">Sessions</th>
              <th className="text-right px-2 py-1">Avg score</th>
              <th className="text-right px-2 py-1">Last attempt</th>
            </tr>
          </thead>
          <tbody>
            {state.rows.map((u) => (
              <tr key={u.userId} className="border-t border-gray-200">
                <td className="px-2 py-1 text-gray-400">{u.rank}</td>
                <td className="px-2 py-1">
                  <span className="font-medium text-gray-800">{u.username}</span>
                  {u.fullname && <span className="text-gray-500"> · {u.fullname}</span>}
                </td>
                <td className="px-2 py-1 text-right font-medium">{u.questionsAnswered}</td>
                {/* Attempts counts retries too, so it can exceed Questions. */}
                <td className="px-2 py-1 text-right text-gray-500">{u.attempts}</td>
                <td className="px-2 py-1 text-right text-gray-500">{u.sessions}</td>
                <td className="px-2 py-1 text-right">{u.averageScorePercent}%</td>
                <td className="px-2 py-1 text-right text-gray-500">{formatDate(u.lastAttemptAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderChapters = (bookId) => {
    const state = chapters[bookId];
    if (!state || state.loading) {
      return <p className="text-sm text-gray-400 px-4 py-3">Loading chapters…</p>;
    }
    if (state.error) {
      return <p className="text-sm text-red-600 px-4 py-3">{state.error}</p>;
    }
    if (!state.rows.length) {
      return <p className="text-sm text-gray-400 px-4 py-3">This book has no chapters yet.</p>;
    }

    return (
      <div className="px-4 py-3">
        {state.summary && (
          <p className="text-xs text-gray-500 mb-2">
            {state.summary.subscribers} subscribed ·{" "}
            {state.summary.chaptersStarted} of {state.summary.totalChapters} chapters started
          </p>
        )}
        <table className="w-full text-xs bg-white rounded border">
          <thead className="bg-gray-100 text-gray-600">
            <tr>
              <th className="text-left px-2 py-1.5">Chapter</th>
              <th className="text-right px-2 py-1.5">Started by</th>
              <th className="text-right px-2 py-1.5">Completed by</th>
              <th className="text-right px-2 py-1.5">Questions answered</th>
              <th className="text-right px-2 py-1.5">Questions in chapter</th>
              <th className="text-right px-2 py-1.5">Avg score</th>
              <th className="text-right px-2 py-1.5">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {state.rows.map((ch) => (
              <React.Fragment key={ch.chapterId}>
                <tr
                  className={`border-t cursor-pointer hover:bg-blue-50 ${
                    openChapter === ch.chapterId ? "bg-blue-50" : ""
                  }`}
                  onClick={() => toggleChapter(ch.chapterId)}
                  title="Click for the top students in this chapter"
                >
                  <td className="px-2 py-1.5">
                    <span className="text-gray-400 mr-1">
                      {openChapter === ch.chapterId ? "▾" : "▸"}
                    </span>
                    {ch.title}
                  </td>
                  <td className="px-2 py-1.5 text-right font-medium">{ch.learners}</td>
                  <td className="px-2 py-1.5 text-right">{ch.completedBy}</td>
                  <td className="px-2 py-1.5 text-right">{ch.questionsAnswered}</td>
                  <td className="px-2 py-1.5 text-right text-gray-500">{ch.totalQuestions}</td>
                  <td className="px-2 py-1.5 text-right">{ch.averageScorePercent}%</td>
                  <td className="px-2 py-1.5 text-right text-gray-500">
                    {formatDate(ch.lastActivityAt)}
                  </td>
                </tr>
                {openChapter === ch.chapterId && (
                  <tr className="bg-gray-50">
                    <td colSpan={7} className="px-3 py-2">{renderTopUsers(ch.chapterId)}</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b">
        <div>
          <h2 className="font-semibold text-gray-700">Books &amp; chapters</h2>
          <p className="text-xs text-gray-500 mt-1">
            Click a book for its chapters, then a chapter for its top students.
          </p>
        </div>
        <form onSubmit={submitSearch} className="flex gap-2">
          <input
            className="border rounded px-3 py-1.5 text-sm"
            placeholder="Search title or subject"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit" className="bg-gray-800 text-white px-3 py-1.5 rounded text-sm">
            Search
          </button>
        </form>
      </div>

      {error && (
        <p className="px-4 py-3 text-sm text-red-600">{error}</p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              {BOOK_COLUMNS.map((c) => (
                <th
                  key={c.field}
                  onClick={() => toggleSort(c.field)}
                  className={`px-4 py-2 cursor-pointer select-none hover:text-gray-900 ${c.align}`}
                  title="Click to sort"
                >
                  {c.label} {sortBy === c.field && (order === "asc" ? "▲" : "▼")}
                </th>
              ))}
              <th className="text-right px-4 py-2">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
            )}
            {!loading && books.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">No books found</td></tr>
            )}
            {!loading && books.map((b) => (
              <React.Fragment key={b.bookId}>
                <tr
                  className={`border-t cursor-pointer hover:bg-gray-50 ${
                    openBook === b.bookId ? "bg-gray-50" : ""
                  }`}
                  onClick={() => toggleBook(b.bookId)}
                >
                  <td className="px-4 py-2">
                    <span className="text-gray-400 mr-1">
                      {openBook === b.bookId ? "▾" : "▸"}
                    </span>
                    <span className="font-medium text-gray-800">{b.title}</span>
                    <div className="text-xs text-gray-500 ml-4">
                      {[b.subject, b.grade && `Grade ${b.grade}`, b.publisher]
                        .filter(Boolean)
                        .join(" · ")}
                      {b.isHidden && <span className="text-amber-600"> · hidden</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right font-medium">{b.subscribers}</td>
                  <td className="px-4 py-2 text-right">
                    {b.chaptersStarted}
                    <span className="text-gray-400"> / {b.totalChapters}</span>
                  </td>
                  <td className="px-4 py-2 text-right">{b.learners}</td>
                  <td className="px-4 py-2 text-right">{b.questionsAnswered}</td>
                  <td className="px-4 py-2 text-right">{b.averageScorePercent}%</td>
                  <td className="px-4 py-2 text-right text-gray-500 whitespace-nowrap">
                    {formatDate(b.lastActivityAt)}
                  </td>
                </tr>
                {openBook === b.bookId && (
                  <tr className="bg-gray-50">
                    <td colSpan={7} className="p-0">{renderChapters(b.bookId)}</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between p-4 border-t text-sm">
          <span className="text-gray-500">
            Page {pagination.page} of {pagination.totalPages} · {pagination.total} books
          </span>
          <div className="flex gap-2">
            <button
              disabled={!pagination.hasPrev}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              Previous
            </button>
            <button
              disabled={!pagination.hasNext}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookChapterAnalytics;
